import { Link } from "@tanstack/react-router";
import { createRoute } from "@tanstack/react-router";
import { Bell } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "@/hooks/useSafeTranslation";
import { toast } from "@/lib/safe-toast";
import { authenticatedRoute } from "../_authenticated";
import { Card, CardContent, CardDescription, CardHeader, CardTitle, Input, Button } from "@/components/ui";
import { toErrorDisplayMessage, toDisplayString, toSafeReactNode } from "@/lib/utils";
import {
  useCreateReminder,
  useDeleteReminder,
  useReminders,
  useUpdateReminder,
} from "@/hooks/useReminders";
import { useAuth } from "@/contexts/useAuth";
import { usePaywallShown } from "@/hooks/usePaywallShown";

function RemindersPage() {
  const { t, i18n } = useTranslation("common");
  const [title, setTitle] = useState("");
  const [note, setNote] = useState("");
  const [remindAt, setRemindAt] = useState("");

  const { state } = useAuth();
  const token = state.status === "authenticated" ? state.token : null;
  const { data: remindersData, isLoading, error } = useReminders(true);
  const reminders = remindersData?.reminders ?? [];
  const premiumRequired = remindersData?.premiumRequired ?? false;
  const locale = i18n.language || "en";

  // The same flag the offer below is rendered from. See hooks/usePaywallShown.ts.
  usePaywallShown(premiumRequired, token, "premium_required");
  const createReminder = useCreateReminder(true);
  const updateReminder = useUpdateReminder(true);
  const deleteReminder = useDeleteReminder(true);

  const onCreate = async () => {
    if (!title.trim() || !remindAt) return;
    try {
      const asIso = new Date(remindAt).toISOString();
      await createReminder.mutateAsync({
        title: title.trim(),
        note: note.trim() || undefined,
        remindAt: asIso,
      });
      setTitle("");
      setNote("");
      setRemindAt("");
      toast.success(t("reminders.createSuccess"));
    } catch (e) {
      toast.error(toErrorDisplayMessage(e) || t("reminders.createError"));
    }
  };

  const onToggleComplete = async (id: string, completed: boolean) => {
    try {
      await updateReminder.mutateAsync({ id, completed: !completed });
    } catch (e) {
      toast.error(toErrorDisplayMessage(e) || t("reminders.updateError"));
    }
  };

  const onDelete = async (id: string) => {
    try {
      await deleteReminder.mutateAsync(id);
      toast.success(t("reminders.deleteSuccess"));
    } catch (e) {
      toast.error(toErrorDisplayMessage(e) || t("reminders.deleteError"));
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-semibold tracking-tight text-text">{toSafeReactNode(t("reminders.title"))}</h1>
        <p className="text-sm text-text-muted">{toSafeReactNode(t("reminders.subtitle"))}</p>
      </div>

      <Card>
        <CardHeader>
          <Bell className="mb-2 h-8 w-8 text-text-muted" aria-hidden />
          <CardTitle>{toSafeReactNode(t("reminders.cardTitle"))}</CardTitle>
          <CardDescription>{toSafeReactNode(t("reminders.cardDescription"))}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {!premiumRequired && (
            <div className="grid gap-2 md:grid-cols-3">
              <Input
                type="text"
                placeholder={toDisplayString(t("reminders.titlePlaceholder"))}
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
              <Input
                type="datetime-local"
                value={remindAt}
                onChange={(e) => setRemindAt(e.target.value)}
              />
              <div className="flex gap-2">
                <Input
                  type="text"
                  placeholder={toDisplayString(t("reminders.notePlaceholder"))}
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                />
                <Button
                  type="button"
                  variant="primary"
                  onClick={onCreate}
                  disabled={createReminder.isPending || !title.trim() || !remindAt}
                >
                  {createReminder.isPending ? "…" : toSafeReactNode(t("reminders.create"))}
                </Button>
              </div>
            </div>
          )}

          {premiumRequired && (
            <div className="rounded-md border border-border bg-surface-2 p-3 text-sm text-text-muted">
              {toSafeReactNode(t("reminders.premiumFeature"))}
              <div className="mt-2">
                <Link to="/billing" className="text-accent hover:underline">
                  {toSafeReactNode(t("reminders.upgradeNow"))}
                </Link>
              </div>
            </div>
          )}
          {isLoading && <p className="text-sm text-text-muted">{toSafeReactNode(t("reminders.loading"))}</p>}
          {error && <p className="text-sm text-danger">{toErrorDisplayMessage(error)}</p>}
          {reminders.length === 0 && !premiumRequired && (
            <p className="text-sm text-text-muted">{toSafeReactNode(t("reminders.none"))}</p>
          )}
          {reminders.length > 0 && (
            <ul className="space-y-2">
              {reminders.map((item) => (
                <li
                  key={toDisplayString(item.id)}
                  className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-border bg-surface-2 px-3 py-2"
                >
                  <div className="min-w-0">
                    <p className={`truncate text-sm ${item.completed ? "text-text-muted line-through" : "text-text"}`}>
                      {toSafeReactNode(item.title)}
                    </p>
                    <p className="truncate text-xs text-text-muted">
                      {new Date(item.remindAt).toLocaleString(toDisplayString(locale))}
                      {item.note ? ` · ${toSafeReactNode(item.note)}` : ""}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={() => onToggleComplete(item.id, item.completed)}
                      disabled={updateReminder.isPending}
                    >
                      {item.completed ? toSafeReactNode(t("reminders.markActive")) : toSafeReactNode(t("reminders.markDone"))}
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={() => onDelete(item.id)}
                      disabled={deleteReminder.isPending}
                    >
                      {toSafeReactNode(t("reminders.delete"))}
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export const remindersRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "reminders",
  component: RemindersPage,
});
